import cloneDeep from "lodash.clonedeep";
import debounce from "lodash.debounce";
import Vue from "vue";
import { Module } from "vuex";

import { AppointmentsApi } from "@/api/appointment";
import { CheckoutApi } from "@/api/checkout";
import { PatientsApi } from "@/api/patients";
import { PractitionersAPI } from "@/api/practitioners";
import { VisitNoteAPI } from "@/api/visitNote";
import { CheckoutResponseDto } from "@/store/modules/checkout/types";
import {
  initialEncounterNotes,
  initialMedicalInfo,
  initialPatientInfo,
  initialVisitDescription,
} from "@/store/modules/visitNote/initialState";
import { VisitNoteState } from "@/store/modules/visitNote/interfaces/VisitNoteState";
import { RootState } from "@/types";

export const visitNote: Module<VisitNoteState, RootState> = {
  namespaced: true,
  state: {
    patientInfo: cloneDeep(initialPatientInfo),
    medicalInfo: cloneDeep(initialMedicalInfo),
    encounterNotes: cloneDeep(initialEncounterNotes),
    otherInfo: {
      patientEmail: "",
      appointmentId: "",
      visitNoteId: "",
      patientPhoneNumber: "",
      status: "",
      practitionerId: undefined,
      practitionerEmail: "",
      practitionerPhoneNumber: "",
    },
    visitDescription: initialVisitDescription,
    checkout: undefined,
  },
  mutations: {
    setPatientInfo(state: VisitNoteState, payload): void {
      state.patientInfo = payload;
    },
    setMedicalInfo(state: VisitNoteState, payload): void {
      state.medicalInfo = payload;
    },
    setEncounterNotes(state: VisitNoteState, payload): void {
      state.encounterNotes = payload;
    },
    setPatientInfoField(state: VisitNoteState, payload): void {
      const { value, fieldName } = payload;
      Vue.set(state.patientInfo, fieldName, value);
    },
    setMedicalInfoField(state: VisitNoteState, payload): void {
      const { value, fieldName } = payload;
      Vue.set(state.medicalInfo, fieldName, value);
    },
    setEncounterNotesField(state: VisitNoteState, payload): void {
      const { value, fieldName } = payload;
      Vue.set(state.encounterNotes, fieldName, value);
    },
    setPatientEmail(state: VisitNoteState, payload): void {
      state.otherInfo.patientEmail = payload;
    },
    setAppointmentId(state: VisitNoteState, payload): void {
      state.otherInfo.appointmentId = payload;
    },
    setVisitNoteId(state: VisitNoteState, payload): void {
      state.otherInfo.visitNoteId = payload;
    },
    setVisitDescription(state: VisitNoteState, payload): void {
      state.visitDescription = payload;
    },
    setPatientPhoneNumber(state: VisitNoteState, payload): void {
      state.otherInfo.patientPhoneNumber = payload;
    },
    setStatus(state: VisitNoteState, payload): void {
      Vue.set(state.otherInfo, "status", payload);
    },
    setPractitionerEmail(state: VisitNoteState, payload): void {
      Vue.set(state.otherInfo, "practitionerEmail", payload);
    },
    setPractitionerPhoneNumber(state: VisitNoteState, payload): void {
      Vue.set(state.otherInfo, "practitionerPhoneNumber", payload);
    },
    setPractitionerId(state: VisitNoteState, payload): void {
      Vue.set(state.otherInfo, "practitionerId", payload);
    },
    setCheckout(state: VisitNoteState, payload: CheckoutResponseDto): void {
      state.checkout = payload;
    },
  },
  actions: {
    async getVisitNote({ getters, commit }) {
      const { appointmentId, visitDescription } = getters;
      const [
        { id, requestInformation, status, patientId, patientInformation, medicalInformation, encounterNotes },
      ] = await VisitNoteAPI.getById(appointmentId);
      commit("setVisitNoteId", id);
      const patient = await PatientsApi.patientGetProfile(patientId);
      commit("setStatus", status);
      commit("setPatientEmail", patient.email);
      commit("setPatientPhoneNumber", patient.phoneNumber);
      commit("setVisitDescription", {
        ...visitDescription,
        ...requestInformation.medicalInformation,
        ...requestInformation.generalInformation,
        complaintDescription: requestInformation.complaintDescription,
      });

      if (!patientInformation && !medicalInformation && !encounterNotes) {
        commit("setPatientInfo", {
          firstName: patient.firstName,
          lastName: patient.lastName,
          gender: patient.gender,
          phoneNumber: patient.phoneNumber,
          state: patient.state,
        });
        commit("setEncounterNotes", {
          ...initialEncounterNotes,
          chiefComplaint: requestInformation.chiefComplaint,
        });
        commit("setMedicalInfo", {
          psh: [],
          allergies: requestInformation.medicalInformation.allergies || [],
          medications: requestInformation.medicalInformation.medications || [],
          pmh: requestInformation.medicalInformation.pmh || [],
        });
      } else {
        commit("setPatientInfo", patientInformation);
        commit("setEncounterNotes", encounterNotes);
        commit("setMedicalInfo", {
          ...medicalInformation,
          medications: medicalInformation.medications?.map((m: any) => ({
            ...m,
            startDate: new Date(m.startDate),
          })),
        });
      }
    },
    async signVisitNote({ getters }) {
      const { visitNoteId } = getters;
      await VisitNoteAPI.sign(visitNoteId);
    },
    async getVisitSummary({ commit, getters, rootGetters }) {
      const { appointmentId } = getters;
      const {
        email,
        "encounters/currentEncounter": { practitioner },
      } = rootGetters;
      const currentPractitioner = await PractitionersAPI.getPractitionerById(practitioner.id);
      const [summary] = await AppointmentsApi.getSummary(appointmentId);
      const { patientInformation, encounterNotes } = summary;
      commit("setPractitionerPhoneNumber", currentPractitioner.phoneNumber);
      commit("setPractitionerEmail", currentPractitioner.email);
      commit("setPractitionerId", currentPractitioner.id);
      commit("setPatientInfo", patientInformation);
      commit("setPatientEmail", email);
      commit("setEncounterNotes", encounterNotes);
      const checkout = await CheckoutApi.getByAppointment(appointmentId);
      commit("setCheckout", checkout);
    },
    autosave({ dispatch }) {
      debounce(() => {
        dispatch("saveVisitNote");
      }, 2000)();
    },
    savePatientInfo({ commit }, data) {
      commit("setPatientInfo", data);
    },
    async saveVisitNote({ getters }) {
      const { patientInfo, medicalInfo, encounterNotes, visitNoteId } = getters;
      await VisitNoteAPI.update(visitNoteId, {
        encounterNotes,
        medicalInformation: medicalInfo,
        patientInformation: patientInfo,
      });
    },
    clearPatientInfoFields(ctx) {
      ctx.commit("setPatientInfo", initialPatientInfo);
    },
    clearMedicalInfoFields(ctx) {
      ctx.commit("setMedicalInfo", initialMedicalInfo);
    },
    clearEncounterNoteFields(ctx) {
      ctx.commit("setEncounterNotes", initialEncounterNotes);
    },
  },
  getters: {
    patientInfo: (state) => state.patientInfo,
    checkout: (state: VisitNoteState): CheckoutResponseDto | undefined => state.checkout,
    medicalInfo: (state) => state.medicalInfo,
    encounterNotes: (state) => state.encounterNotes,
    patientEmail: (state) => state.otherInfo.patientEmail,
    patientPhoneNumber: (state) => state.otherInfo.patientPhoneNumber,
    appointmentId: (state) => state.otherInfo.appointmentId,
    visitNoteId: (state) => state.otherInfo.visitNoteId,
    visitDescription: (state) => state.visitDescription,
    status: (state) => state.otherInfo.status,
    practitionerPhoneNumber: (state) => state.otherInfo.practitionerPhoneNumber,
    practitionerEmail: (state) => state.otherInfo.practitionerEmail,
    practitionerId: (state) => state.otherInfo.practitionerId,
    disabledSigning: ({ patientInfo, encounterNotes }, getters, rootState, rootGetters) => {
      const roomIsFinished = rootGetters["videoRoom/roomIsFinished"];
      if (!roomIsFinished) {
        return true;
      }
      const checksArray = [
        patientInfo.firstName,
        patientInfo.lastName,
        patientInfo.state,
        patientInfo.phoneNumber,
        encounterNotes.dx,
      ];
      return checksArray.some((el) => !el);
    },
  },
};
